<script lang="ts">
	import Checkbox from '@astlai/common/Checkbox.svelte';
	import LoadingIndicator from '@astlai/common/LoadingIndicator.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { Fetchable } from '#lib/util/fetchable';

	interface Props {
		showEmailInput?: boolean;
		showNewsletterCheckbox?: boolean;
		onsubmit: (name: string, email?: string, emailOptIn?: boolean) => Promise<void>;
		/**
		 * The user would rather change the image than save it now — no submission, just a normal chat reply.
		 * Without it, there's no option to decline.
		 */
		ondecline?: () => void;
	}

	const {
		showEmailInput = true,
		showNewsletterCheckbox = false,
		onsubmit,
		ondecline
	}: Props = $props();

	let name = $state('');
	let email = $state('');
	let emailOptIn = $state(false);
	let submitted = $state(false);
	const fetchable = new Fetchable();

	async function submit() {
		if (!name.trim()) {
			return;
		}
		await fetchable.fetch(async () => {
			await onsubmit(
				name.trim(),
				showEmailInput && email.trim() ? email.trim() : undefined,
				showEmailInput ? emailOptIn : undefined
			);
			submitted = true;
		});
	}

	function handleFormSubmit(e: Event) {
		e.preventDefault();
		submit();
	}
</script>

{#if !submitted}
	<form class="antonio-contact-input" onsubmit={handleFormSubmit}>
		<div class="inputs">
			<input
				type="text"
				bind:value={name}
				placeholder={t('antonio.contact.namePlaceholder')}
				disabled={fetchable.fetching}
				required
			/>
			{#if showEmailInput}
				<!-- pattern: the server's check (emailRegex, backend/routes/event_signup.go), which also wants a dot in the domain. -->
				<input
					type="email"
					pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
					bind:value={email}
					placeholder={t('antonio.contact.emailPlaceholder')}
					disabled={fetchable.fetching}
				/>
			{/if}
			{#if showEmailInput && showNewsletterCheckbox}
				<Checkbox bind:checked={emailOptIn} disabled={fetchable.fetching}>
					{t('antonio.contact.newsletterOptIn')}
				</Checkbox>
			{/if}
			<div class="actions">
				<button class="accent large" type="submit" disabled={fetchable.fetching}>
					{#if fetchable.fetching}
						<LoadingIndicator />
					{:else}
						{t('antonio.publishConfirm.yes.label')}
					{/if}
				</button>
				{#if ondecline}
					<button class="large" type="button" disabled={fetchable.fetching} onclick={ondecline}>
						{t('antonio.publishConfirm.no.label')}
					</button>
				{/if}
			</div>
		</div>
	</form>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.antonio-contact-input {
		@include lib.flex-column();
		gap: 0.5rem;
		padding: 0.5rem 0;

		.inputs {
			@include lib.flex-column();
			gap: 0.5rem;
			padding: 0.5rem;
			border: 1px solid var(--border-subtle);
			border-radius: 0.75rem;

			// Same layout as AntonioButtons' action row.
			.actions {
				@include lib.flex-row();
				flex-wrap: wrap;
				gap: 0.5rem;

				button {
					&:first-child {
						flex: 1 1 66%;
					}
					flex: 1 1 33%;
				}
			}
		}
	}
</style>
